// Manages: sessionId, saveStatus, savedAt, the stateRef mirror, and the
// debounce-free saveDraft() that POST→/sessions or PATCH→/sessions/{id} with
// the current snapshot of all session-related state.
//
// Extracted from NewSession.jsx during Iter AN Phase 2. Behaviour preserved
// exactly — failure re-tries after 5 s, status flips through idle→saving→
// (saved|error)→saved, savedAt label refreshes every 15 s while idle/saved.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";


export function useSessionDraft({ clientId, resumeId }) {
  const [sessionId, setSessionId] = useState(resumeId || null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);

  // Mirror of the latest state — every consumer updates this through
  // `setStateSnapshot` once per render so saveDraft() always sees fresh values.
  const stateRef = useRef({});
  const setStateSnapshot = useCallback((snap) => { stateRef.current = snap; }, []);

  const saveDraft = useCallback(async (patch = {}, opts = {}) => {
    const { silent = false } = opts;
    const cur = stateRef.current;
    if (!silent) setSaveStatus("saving");
    try {
      const body = {
        front_image: cur.photos?.front,
        side_image: cur.photos?.side,
        back_image: cur.photos?.back,
        // Mirror the guided 4-view capture into the EXISTING SessionPhotos baseline
        // slots (front / right / back / left). Merge over the current photos subdoc
        // so post-treatment (post_*) photos are never clobbered.
        photos: {
          ...(cur.sessionPhotos || {}),
          baseline_front: cur.photos?.front,
          baseline_right_side: cur.photos?.side,
          baseline_back: cur.photos?.back,
          baseline_left_side: cur.photos?.left_side,
        },
        intake_notes: cur.intakeNotes || null,
        ai_analysis: cur.analysis,
        worked_areas: cur.workedAreas,
        wrap_up: cur.wrapUp,
        progress_since_last: cur.progressSinceLast,
        soap: cur.soap,
        outcome: cur.outcome,
        billing: cur.billing,
        protocol_id: cur.selectedProtocolId,
        duration_minutes: cur.duration,
        session_type: cur.sessionType,
        assessment_mode: cur.assessmentMode || null,
        photo_mode: cur.photoMode,
        compared_from_session_id: cur.comparedFromSessionId,
        photo_comparison_summary: cur.photoComparisonSummary || null,
        treatment_map: cur.treatmentMap,
        chief_complaint: cur.chiefComplaintObj?.chief_complaint || null,
        chief_complaint_area: cur.chiefComplaintObj?.chief_complaint_area || null,
        secondary_concerns: cur.chiefComplaintObj?.secondary_concerns || [],
        issue_type: cur.chiefComplaintObj?.issue_type || null,
        ...patch,
      };
      if (!cur.sessionId) {
        const r = await api.post("/sessions", { client_id: clientId, status: "draft", ...body });
        setSessionId(r.data.id);
      } else {
        await api.patch(`/sessions/${cur.sessionId}`, body);
      }
      setSavedAt(new Date());
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
      setTimeout(() => saveDraft(patch, { silent: true }), 5000);
    }
  }, [clientId]);

  // Periodically refresh the "5 s ago" label.
  useEffect(() => {
    if (saveStatus !== "saved") return undefined;
    const t = setInterval(() => setSavedAt((d) => (d ? new Date(d.getTime()) : d)), 15000);
    return () => clearInterval(t);
  }, [saveStatus]);

  return {
    sessionId, setSessionId,
    saveStatus, setSaveStatus,
    savedAt, setSavedAt,
    saveDraft,
    setStateSnapshot,
    // expose for hooks that need the live snapshot (e.g. useSessionAI)
    stateRef,
  };
}


// Tiny helper: keep the stateRef mirror up-to-date once per render.
// Use it inside the parent component with the full state bundle.
export function useStateSnapshot(setStateSnapshot, snapshot) {
  useLayoutEffect(() => { setStateSnapshot(snapshot); });
}
